import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { parseGeoCSV } from "../../data/geoOptions";
import { searchGeos, fetchGeosByKeys, validateZipCode, parseZipCSV, buildZipGeoObject, resolveGeoList, isGeoList } from "../../services/geoService";
import { SelectedGeoGroups } from "../../components/SelectedGeoGroups";
import { SR_ONLY } from "../../constants/ui";

// Shared shell for the geo typeahead dropdowns (list-paste panel and the search listbox);
// each site spreads this and overrides only maxHeight/padding.
const geoDropdownStyle = { position:"absolute", top:"100%", left:0, right:0, background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:8, zIndex:20, overflowY:"auto", marginTop:4, boxShadow:"0 4px 12px rgba(0,0,0,0.10)" };

// The small colored note after a search row's name ("Included", "Already added").
const rowTagStyle = { marginLeft:6, fontSize:10 };

// One geo list of the campaign settings drawer: its heading, chips and the search/paste/CSV
// editor. BudgetDrawer renders it once for the advertiser's geos and once, for support only,
// for the measurement geos.
// - title: the uppercase heading (without the "(States & DMAs…)" suffix) and the list's name in
//   the Edit button's label ("Edit <title>").
// - geos / setGeos: the list and its React state setter (called with an updater).
// - takenIds: ids already in the other list, so one geo is never saved twice in the geo column.
//   Without onClaim they are offered as "Already added".
// - onClaim(geos) + claimLabel: adding a geo from takenIds moves it into this list instead
//   (support's measurement list); its search row reads claimLabel.
// - dnd: { onDragStart(geo), onDragEnd(), accepts(), onDrop() } lets support drag chips
//   between the two editors. Omit it for a list that takes no drags.
// - idPrefix: prefix of the dropdowns' DOM ids, distinct per editor on the screen.
// - labelPrefix: passed to SelectedGeoGroups so two editors' chip lists have distinct names.
// - notice: rendered between the header and the editor (the feature-flag error).
export function GeoEditor({
  title, geos, setGeos, takenIds, onClaim, claimLabel, dnd, idPrefix, labelPrefix,
  zipEnabled, canEdit, editBtnStyle, labelStyle, notice, style,
}) {
  const [dropActive, setDropActive] = useState(false);
  const [geoOpen, setGeoOpen]         = useState(false);
  // Matched by object identity, not id: legacy synced geos can lack an id, and matching on
  // `undefined` would remove or toggle every id-less geo at once.
  const removeGeo = useCallback(geo => setGeos(prev => prev.filter(g => g !== geo)), [setGeos]);
  // A missing clusivity is displayed as included (the bidder default), so it flips to exclude.
  const toggleGeoClusivity = useCallback(geo => setGeos(prev => prev.map(g =>
    g === geo ? { ...g, clusivity: g.clusivity === "exclude" ? "include" : "exclude" } : g
  )), [setGeos]);
  const [geoResults, setGeoResults]   = useState([]);
  const [geoAddAsExclude, setGeoAddAsExclude] = useState(false);
  const [geoTypeahead, setGeoTypeahead] = useState("");
  const geoInputRef = useRef(null);
  const [activeGeoIndex, setActiveGeoIndex] = useState(-1);

  useEffect(() => {
    setActiveGeoIndex(-1);
    if (!geoTypeahead.trim()) { setGeoResults([]); return; }
    setGeoResults(searchGeos(geoTypeahead, ["state", "dma"]));
  }, [geoTypeahead]);

  const geoClusivity = geoAddAsExclude ? "exclude" : "include";
  const geoById = useMemo(() => new Map(geos.map(g => [g.id, g])), [geos]);
  // A geo is taken when this list already has its id, or the other one does and this list
  // cannot claim it, so one geo is never saved twice in the geo column.
  const isTaken = useCallback(id => geoById.has(id) || (!onClaim && takenIds.has(id)), [geoById, takenIds, onClaim]);
  // The note on a search row for a geo in the other list.
  const otherListTag = id => (!geoById.has(id) && takenIds.has(id) ? (onClaim ? claimLabel : "Already added") : null);

  // Resolving a pasted/typed list is exact-match against the full geo set, which is more
  // work than the substring search above — memoize so it only re-runs when the typed text
  // or zip-eligibility actually changes, not on chip edits or unrelated drawer re-renders.
  const geoListResolution = useMemo(() => {
    if (!isGeoList(geoTypeahead, ["state", "dma"])) return null;
    return resolveGeoList(geoTypeahead, ["state", "dma"], { includeZip: zipEnabled });
  }, [geoTypeahead, zipEnabled]);
  const geoList = useMemo(() => {
    if (!geoListResolution) return null;
    const matched = geoListResolution.matched.map(g => ({ ...g, clusivity: geoClusivity }));
    return { matched, unmatched: geoListResolution.unmatched, newGeos: matched.filter(g => !isTaken(g.id)) };
  }, [geoListResolution, geoClusivity, isTaken]);

  const geoTrimmed = geoTypeahead.trim();
  const geoIsList = geoList != null;
  const geoListMatched = geoList?.matched ?? [];
  const geoListUnmatched = geoList?.unmatched ?? [];
  const geoListNewGeos = geoList?.newGeos ?? [];
  const geoZipOpt = geoTrimmed && zipEnabled && validateZipCode(geoTrimmed)
    ? buildZipGeoObject(geoTrimmed, geoClusivity)
    : null;
  // The search dropdown's rows in display order: the typed zip code first, then the matches.
  const searchOptions = [geoZipOpt, ...geoResults.map(o => ({ ...o, clusivity: geoClusivity }))].filter(Boolean);

  const resetTypeahead = () => { setGeoTypeahead(""); setActiveGeoIndex(-1); };
  // Adds the geos this list lacks, once each. One from the other list is moved here when this
  // list can claim it, and skipped otherwise.
  const addGeos = list => {
    const unique = [...new Map(list.map(g => [g.id, g])).values()];
    const claimed = unique.filter(g => takenIds.has(g.id));
    if (onClaim && claimed.length) onClaim(claimed);
    const fresh = unique.filter(g => !takenIds.has(g.id));
    setGeos(prev => {
      const have = new Set(prev.map(g => g.id));
      return [...prev, ...fresh.filter(g => !have.has(g.id))];
    });
  };
  const addGeo = geo => {
    if (geo && !isTaken(geo.id)) addGeos([geo]);
    resetTypeahead();
  };
  const handleAddAllGeos = () => {
    addGeos(geoListNewGeos);
    resetTypeahead();
  };
  const dropProps = dnd && {
    onDragOver: e => {
      if (!dnd.accepts()) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
      setDropActive(true);
    },
    onDragLeave: e => { if (!e.currentTarget.contains(e.relatedTarget)) setDropActive(false); },
    onDrop: e => { e.preventDefault(); setDropActive(false); dnd.onDrop(); },
  };

  const listPanelId = `${idPrefix}-list-panel`;
  const listboxId = `${idPrefix}-listbox`;
  const optionId = i => `${idPrefix}-option-${i}`;

  return (
    <div {...dropProps} style={{ ...style, position:"relative", ...(dropActive && { outline:"2px dashed #F90091", outlineOffset:4, borderRadius:8 }) }}>
      <div style={labelStyle}>
        {title} (States & DMAs{zipEnabled ? " & Zip Codes" : ""})
      </div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
        <span style={{ color:"#151515", fontSize:13, fontWeight:500 }}>Include or exclude geos</span>
        <button onClick={() => canEdit && setGeoOpen(!geoOpen)} disabled={!canEdit} aria-label={geoOpen ? `Done editing ${title}` : `Edit ${title}`} style={editBtnStyle}>{geoOpen ? "Done" : "Edit"}</button>
      </div>
      {notice}
      {geoOpen && (
        <>
          <SelectedGeoGroups geos={geos} onRemove={removeGeo} onToggle={toggleGeoClusivity} labelPrefix={labelPrefix} onDragStart={dnd?.onDragStart} onDragEnd={dnd?.onDragEnd} />
          <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:6 }}>
            <span style={{ fontSize:11, color:"#7b7b7b" }}>Add as:</span>
            <button type="button" onClick={() => setGeoAddAsExclude(false)} style={{ padding:"4px 10px", borderRadius:6, fontSize:12, border:"1px solid #E2E2E2", background: !geoAddAsExclude ? "#EEF2FF" : "transparent", color: !geoAddAsExclude ? "#323084" : "#626262", cursor:"pointer" }}>Include</button>
            <button type="button" onClick={() => setGeoAddAsExclude(true)} style={{ padding:"4px 10px", borderRadius:6, fontSize:12, border:"1px solid #E2E2E2", background: geoAddAsExclude ? "#FFF5F5" : "transparent", color: geoAddAsExclude ? "#e53935" : "#626262", cursor:"pointer" }}>Exclude</button>
          </div>
          <div style={{ position:"relative", marginBottom:8 }}>
            <input
              ref={geoInputRef}
              type="text"
              value={geoTypeahead}
              onChange={e => setGeoTypeahead(e.target.value)}
              onPaste={e => {
                const text = e.clipboardData.getData("text");
                if (!/[\r\n]/.test(text)) return;
                e.preventDefault();
                // Join with "; " (a hard separator), not ",": an <input type="text">
                // strips line breaks anyway, and joining with a comma would let two
                // pasted lines (e.g. a spreadsheet column "Albany" / "Ga") rejoin into
                // a single comma-containing geo name ("Albany, Ga").
                const normalized = text.split(/[\r\n]+/).map(s => s.trim()).filter(Boolean).join("; ");
                setGeoTypeahead(normalized);
              }}
              onKeyDown={e => {
                if (geoIsList) {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (geoListNewGeos.length > 0) handleAddAllGeos();
                    return;
                  }
                  if (e.key === "Escape") { e.stopPropagation(); resetTypeahead(); return; }
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") { e.preventDefault(); }
                  return;
                }
                if (e.key === "ArrowDown") { e.preventDefault(); setActiveGeoIndex(i => Math.min(i+1, searchOptions.length-1)); return; }
                if (e.key === "ArrowUp") { e.preventDefault(); setActiveGeoIndex(i => Math.max(i-1, -1)); return; }
                if (e.key === "Escape") { e.stopPropagation(); resetTypeahead(); return; }
                if (e.key !== "Enter") return;
                e.preventDefault();
                if (activeGeoIndex >= 0) { addGeo(searchOptions[activeGeoIndex]); return; }
                // Enter with no row highlighted adds only a typed zip code, and only when it is new.
                if (geoZipOpt && !isTaken(geoZipOpt.id)) addGeo(geoZipOpt);
              }}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={geoTrimmed !== ""}
              aria-controls={geoTrimmed ? (geoIsList ? listPanelId : listboxId) : undefined}
              aria-activedescendant={!geoIsList && activeGeoIndex >= 0 ? optionId(activeGeoIndex) : undefined}
              placeholder={zipEnabled ? "Search states, DMAs or enter zip code…" : "Type to search states or DMAs…"}
              style={{ width:"100%", boxSizing:"border-box", padding:"10px 12px", background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:8, color:"#151515", fontSize:13, outline:"none" }}
            />
            {geoTrimmed !== "" && geoIsList && (
              <div id={listPanelId} style={{ ...geoDropdownStyle, maxHeight:260, padding:12 }}>
                <div role="status" aria-live="polite" aria-atomic="true" style={SR_ONLY}>
                  {geoListMatched.length === 0
                    ? "No matches."
                    : `${geoListMatched.length} match${geoListMatched.length === 1 ? "" : "es"} found.`}
                  {geoListUnmatched.length > 0 ? ` Not found: ${geoListUnmatched.join(", ")}` : ""}
                </div>
                {geoListMatched.length > 0 ? (
                  <>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10 }}>
                      {geoListMatched.map(g => (
                        <span key={g.id} style={{ padding:"4px 8px", background:"#F0F0F0", color:"#151515", borderRadius:6, fontSize:12 }}>
                          {g.display_name || g.name}
                        </span>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={handleAddAllGeos}
                      disabled={geoListNewGeos.length === 0}
                      style={{ padding:"8px 14px", background: geoListNewGeos.length === 0 ? "#F0F0F0" : "#F90091", color: geoListNewGeos.length === 0 ? "#B8B8B8" : "#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:600, cursor: geoListNewGeos.length === 0 ? "default" : "pointer" }}
                    >
                      {geoListNewGeos.length === 0 ? "Already added" : `Add all (${geoListNewGeos.length})`}
                    </button>
                  </>
                ) : (
                  <div style={{ color:"#7b7b7b", fontSize:13 }}>No matches</div>
                )}
                {geoListUnmatched.length > 0 && (
                  <div style={{ marginTop:10, fontSize:12, color:"#7b7b7b" }}>Not found: {geoListUnmatched.join(", ")}</div>
                )}
              </div>
            )}
            {geoTrimmed !== "" && !geoIsList && (() => {
              const hasResults = geoResults.length > 0 || geoZipOpt;
              return (
                <div id={listboxId} role="listbox" style={{ ...geoDropdownStyle, maxHeight:220 }}>
                  {geoZipOpt && (() => {
                    const already = isTaken(geoZipOpt.id);
                    const tag = geoById.has(geoZipOpt.id) ? "Already added" : otherListTag(geoZipOpt.id);
                    const isActive = activeGeoIndex === 0;
                    return (
                      <button
                        type="button" id={optionId(0)} role="option" aria-selected={isActive}
                        onClick={() => { addGeo(geoZipOpt); geoInputRef.current?.focus(); }}
                        style={{ display:"block", width:"100%", textAlign:"left", padding:"10px 12px", fontSize:13, color: already ? "#B8B8B8" : "#151515", cursor: already ? "default" : "pointer", background: isActive ? "#FFF5FB" : already ? "#FAFAFA" : "transparent", border:"none", borderBottom: geoResults.length ? "1px solid #F0F0F0" : "none" }}
                      >
                        {geoTrimmed} <span style={{ fontSize:11, color:"#7b7b7b" }}>(Zip Code)</span>
                        {tag && <span style={{ ...rowTagStyle, color:"#F90091" }}>{tag}</span>}
                      </button>
                    );
                  })()}
                  {geoResults.map((opt, i) => {
                    const flatIdx = geoZipOpt ? i + 1 : i;
                    const existing = geoById.get(opt.id);
                    const tag = otherListTag(opt.id);
                    const alreadySelected = isTaken(opt.id);
                    const isActive = activeGeoIndex === flatIdx;
                    return (
                      <button
                        type="button" key={opt.id} id={optionId(flatIdx)} role="option" aria-selected={isActive}
                        onClick={() => { addGeo(searchOptions[flatIdx]); geoInputRef.current?.focus(); }}
                        style={{ display:"block", width:"100%", textAlign:"left", padding:"10px 12px", fontSize:13, color: alreadySelected ? "#B8B8B8" : "#151515", cursor: alreadySelected ? "default" : "pointer", background: isActive ? "#FFF5FB" : alreadySelected ? "#FAFAFA" : "transparent", border:"none" }}
                      >
                        {opt.display_name || opt.name} <span style={{ fontSize:11, color:"#7b7b7b" }}>({opt.type === "state" ? "State" : "DMA"})</span>
                        {existing?.clusivity === "include" && <span style={{ ...rowTagStyle, color:"#323084" }}>Included</span>}
                        {existing?.clusivity === "exclude" && <span style={{ ...rowTagStyle, color:"#e53935" }}>Excluded</span>}
                        {tag && <span style={{ ...rowTagStyle, color:"#F90091" }}>{tag}</span>}
                      </button>
                    );
                  })}
                  {!hasResults && <div style={{ padding:12, color:"#7b7b7b", fontSize:13 }}>No matches</div>}
                </div>
              );
            })()}
          </div>
          <div style={{ marginTop:8 }}>
            <label style={{ display:"inline-block", padding:"8px 14px", background:"#F0F0F0", color:"#626262", borderRadius:8, fontSize:12, cursor:"pointer" }}>
              Upload CSV{zipEnabled ? " (states, DMAs or zip codes)" : " (states or DMAs)"}
              <input type="file" accept=".csv,.txt" style={{ display:"none" }} onChange={e => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = () => {
                  const text = reader.result;
                  const stateCodes = parseGeoCSV(text);
                  const stateGeos = fetchGeosByKeys(stateCodes.map(c => c.replace("US-", "")), ["state","dma"]).map(g => ({ ...g, clusivity:"include" }));
                  const zipGeos = zipEnabled ? parseZipCSV(text).map(z => buildZipGeoObject(z, "include")) : [];
                  addGeos([...stateGeos, ...zipGeos]);
                };
                reader.readAsText(file);
                e.target.value = "";
              }} />
            </label>
          </div>
        </>
      )}
      {!geoOpen && geos.length > 0 && (
        <div style={{ marginTop:4 }}>
          <SelectedGeoGroups geos={geos} labelPrefix={labelPrefix} onDragStart={dnd?.onDragStart} onDragEnd={dnd?.onDragEnd} />
        </div>
      )}
    </div>
  );
}
